import React from 'react';
import { Button, ConfigProvider } from 'antd';
import { useTranslation } from 'react-i18next';
import Assets from 'assets/icons';
import colors from 'scss/variables/colors.module.scss';
import Spinner from 'components/Spinner/Spinner';
import { athena_state, generateCodeChallenge } from 'helpers/oauth2';
import classes from './VkButton.module.scss';
import { useAppDispatch, useAppSelector } from 'store/hook';
import { setUnlinkModalState } from 'store/slices/windowStateSlice';

const client_id = window.REACT_APP_VK_CLIENT_ID !== 'REPLACE_REACT_APP_VK_CLIENT_ID' ? window.REACT_APP_VK_CLIENT_ID : process.env.REACT_APP_VK_CLIENT_ID;

type VkButtonProps = {
  action: string;
  loading?: boolean;
  preLogic?: () => Promise<boolean>;
}

const VkButton = ({
  action,
  loading,
  preLogic,
}: VkButtonProps) => {
  const [requesting, setRequesting] = React.useState(false);

  const { messageApi } = useAppSelector((state) => state.windowState);
  
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const returnUri = {
    auth:`${window.location.origin}/vk-${action}`,
    register: `${window.location.origin}/vk-${action}`,
    add: window.location.origin,
  }

  const vkLogin = async () => {
    const conditionFulfilled = preLogic ? await preLogic() : true;
    if (conditionFulfilled) {
      setRequesting(true);
      try {
        const code_verifier = athena_state + '_' + Date.now();
        localStorage.setItem('code_verifier', code_verifier);
        const code_challenge = await generateCodeChallenge(code_verifier);
        const redirect_uri = returnUri[action as keyof typeof returnUri];
        const scope = 'vkid.personal_info,email,phone';
        const queryParams = `?response_type=code&client_id=${client_id}&redirect_uri=${redirect_uri}&state=${athena_state}&code_challenge=${code_challenge}&code_challenge_method=S256&scope=${scope}`;
        window.location.assign('https://id.vk.ru/authorize' + queryParams);
      } catch {
        messageApi?.error(t('login.external.errorRequestAuth', {name: t('login.external.btns.vk')}));
        setRequesting(false);
        localStorage.removeItem('code_verifier');
      }
    }
  };

  const handlePageShow = (event: PageTransitionEvent) => {
    if (event.persisted || window.performance.navigation.type === 2) setRequesting(false);
  };

  React.useEffect(() => {
    window.addEventListener('pageshow', handlePageShow);
    return () => {
      window.removeEventListener('pageshow', handlePageShow);
    };
  }, []);

  const deleteExternalAuthModal = () => {
    dispatch(setUnlinkModalState({open: true, service_name: 'vk', in_progress: false}));
  };

  const btnText = {
    auth: t('login.external.btns.login', {name: t('login.external.btns.vk')}),
    register: t('login.external.btns.register', {name: t('login.external.btns.vk')}),
    add: t('login.external.btns.register', {name: t('login.external.btns.vk')}),
    delete: t('login.external.btns.register', {name: t('login.external.btns.vk')}),
  };

  const onButtonClick = {
    auth: () => vkLogin(),
    register: () => vkLogin(),
    add: () => vkLogin(),
    delete: () => deleteExternalAuthModal(),
  }
  
  return (
    <ConfigProvider theme={{ token: { colorPrimary: colors.vk, colorPrimaryHover: colors.vkHover } }}>
      <Button
        type='primary'
        onClick={onButtonClick[action as keyof typeof onButtonClick]}
        className={classes.vkBtn}
        loading={loading || requesting}
      >
        <Assets.VkIcon className={classes.vkIcon}/>
        {btnText[action as keyof typeof btnText]}
        {(loading || requesting) && <Spinner height={'26px'} width={'26px'} color={'white'} size={'22px'}/>}
      </Button>
    </ConfigProvider>
  );
}

export default VkButton;
